import { BrandKit } from 'types'
import { GeneratedContent } from '../../ContentPlanningContext/ContentPlanningContextTypes'
import { DesignContentPreview } from 'components/DesignContentPreview/DesignContentPreview'
import { BrandKitLogo } from 'screens/BrandKit/BrandKitLogo/BrandKitLogo'
import { DatePickerButton } from 'components/Wizards/MobileWizards/Form/DatePickerButton'
import dayjs from 'dayjs'
import Styles from './PostPreviewCard.module.scss'
import PlaceholderAvatar from 'components/Previews/PlaceholderAvatar.png'
import { useRootStore } from 'stores/RootStore'
import { ImageMenuDrawer } from 'components/ImageMenu/ImageMenuDrawer/ImageMenuDrawer'
import { ImageMenuSelectedImage } from 'components/ImageMenu/ImageMenuModal/ImageMenuContext'
import { TemplatesDrawerV2 } from 'components/Templates/TemplatesModalV2/TemplatesDrawerV2'
import { DesignChannel } from 'components/NewFileModal/Types'

export interface PostPreviewCardProps {
  content: GeneratedContent
  brandKit?: BrandKit
  onScheduleChange: (uuid: string, newDate: Date | undefined) => void
  onCaptionEdit: (content: GeneratedContent) => void
  onImageChange: (uuid: string, newImageUrl: string) => void
  onDesignChange: (uuid: string, templateId: number) => void
}

interface PostMediaProps {
  selectedImage?: string
  designContent?: any
  templatePreviewUrl?: string
}

interface PostAccountHeaderProps {
  brandKit?: BrandKit
}

const PostAccountHeader = ({ brandKit }: PostAccountHeaderProps) => {
  return (
    <div className={Styles.postAccountHeader}>
      <div className={Styles.postAvatar}>
        {!!brandKit && <BrandKitLogo brandKit={brandKit} textVariant="label" />}
        {!brandKit && <img src={PlaceholderAvatar} alt="Account Not Connected" />}
      </div>
      <div className={Styles.postHeaderText}>
        <span className={Styles.postPrimaryName}>{brandKit?.name || 'Account Not Connected'}</span>
        <span className={Styles.postSecondaryText}>
          {brandKit?.name?.toLowerCase().replace(/\s+/g, '') || 'account'}
        </span>
        <span className={Styles.postSecondaryText}>1m</span>
      </div>
    </div>
  )
}

const PostMedia = ({
  selectedImage,
  designContent,
  templatePreviewUrl,
  onClick,
}: PostMediaProps & { onClick?: () => void }) => {
  if (!selectedImage && !designContent && !templatePreviewUrl) {
    return null
  }

  if (selectedImage) {
    return (
      <div className={Styles.mediaContainer} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
        <img src={selectedImage} alt="Generated content" className={Styles.postImage} />
      </div>
    )
  }

  if (designContent) {
    return (
      <div className={Styles.mediaContainer} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
        <div className={Styles.designContentContainer}>
          <DesignContentPreview content={designContent} />
        </div>
      </div>
    )
  }

  if (templatePreviewUrl) {
    return (
      <div className={Styles.mediaContainer} onClick={onClick} style={{ cursor: onClick ? 'pointer' : 'default' }}>
        <img src={templatePreviewUrl} alt="Template preview" className={Styles.postImage} />
      </div>
    )
  }

  return null
}

export const PostPreviewCard = ({
  content,
  brandKit,
  onScheduleChange,
  onCaptionEdit,
  onImageChange,
  onDesignChange,
}: PostPreviewCardProps) => {
  const { text, selected_image, content: designContent, postAt, uuid, template, previewUrl } = content
  const { drawersStore } = useRootStore()

  const handleDateTimeChange = (date: Date | undefined) => {
    onScheduleChange(uuid, date)
  }

  const handleCaptionClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    onCaptionEdit(content)
  }

  const handleImageClick = () => {
    drawersStore.openDrawer(ImageMenuDrawer, {
      onUse: (images: ImageMenuSelectedImage[]) => {
        if (images.length > 0) {
          onImageChange(uuid, images[0].url)
          drawersStore.closeDrawer()
        }
      },
      maxCount: 1,
      title: 'Replace Post Image',
      hiddenProviders: ['gifs', 'stockVideo'],
    })
  }

  const handleDesignClick = () => {
    if (!designContent && !template) return

    drawersStore.openDrawer(TemplatesDrawerV2, {
      designChannel: DesignChannel.InstagramPost,
      width: 1080,
      height: 1080,
      disabledFilters: ['format'],
      onUse: () => {},
      onSelect: (templateId: number) => {
        onDesignChange(uuid, templateId)
        drawersStore.closeDrawer()
      },
    })
  }

  const defaultDateTime = postAt?.toDate() || dayjs().add(1, 'day').toDate()

  const getMediaClickHandler = () => {
    if (selected_image) {
      return handleImageClick
    }
    if (designContent || template) {
      return handleDesignClick
    }
    return undefined
  }

  return (
    <div className={Styles.postCard}>
      <PostAccountHeader brandKit={brandKit} />

      <div className={Styles.postContent}>
        <div className={Styles.postText} onClick={handleCaptionClick} style={{ cursor: 'pointer' }}>
          {text}
        </div>
        <PostMedia
          selectedImage={selected_image}
          designContent={designContent}
          templatePreviewUrl={previewUrl}
          onClick={getMediaClickHandler()}
        />
      </div>

      <div className={Styles.scheduleInfo}>
        <DatePickerButton value={defaultDateTime} onChange={handleDateTimeChange} />
      </div>
    </div>
  )
}
